/**
 * EVO — PARTNER TASHKILOTGA KIRIB KO'RISH rejimi.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ EGASINING TALABI                                                         ║
 * ║                                                                          ║
 * ║ «Biz uni kerak paytda uning organizatsiyasiga kirib ko'ra olamiz.»       ║
 * ║                                                                          ║
 * ║ Server buni ALOHIDA token bilan beradi                                   ║
 * ║ (`POST /auth/partner-view/:tenantId`): faqat `.view` bilan tugaydigan    ║
 * ║ ruxsatlar, 30 daqiqa, refresh tokeni YO'Q. Tokenning qayerda            ║
 * ║ saqlanishi va nima uchun refresh yo'liga kirmasligi — `lib/api.ts`       ║
 * ║ dagi ikkita ramkada.                                                     ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ KESH TOZALANADI — HAR O'TISHDA, IKKALA YO'NALISHDA                       │
 * │                                                                          │
 * │ `useQuery` kaliti tenantni BILMAYDI: `['products','list',…]` ikkala      │
 * │ tashkilotda ham bir xil. Tozalanmasa, partnerga kirgan xodim bir necha   │
 * │ soniya BIZNING mahsulotlarimizni partnerniki deb ko'rardi (va teskarisi  │
 * │ — chiqqanda partnerning qoldig'ini o'zimizniki deb). Kalitlarga tenant   │
 * │ qo'shish varianti rad etildi: u butun kod bo'ylab yuzta kalitga tegishi  │
 * │ kerak edi va bittasi unutilsa xato JIMGINA qaytardi. `clear()` esa       │
 * │ bitta joyda va unutib bo'lmaydi.                                         │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { api, isApiError, setPartnerViewExpiredHandler, tokenStore } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useT } from '../../lib/i18n-context';
import { Alert } from '../ui/alert';
import { Button } from '../ui/button';
import { Spinner } from '../ui/spinner';

/** `POST /auth/partner-view/:tenantId` javobi — shartnoma qotirilgan. */
interface PartnerViewResponse {
  readonly accessToken: string;
  /** Server bergan muddat matni (`30m`). Bu yerda TALQIN QILINMAYDI. */
  readonly expiresIn: string;
  readonly tenant: { readonly id: number; readonly code: string; readonly name: string };
}

export interface PartnerViewTenant {
  readonly id: number;
  readonly code: string;
  readonly name: string;
}

interface PartnerViewValue {
  /** `null` — o'z tashkilotimizdamiz. */
  readonly tenant: PartnerViewTenant | null;
  readonly expiresIn: string | null;
  readonly isBusy: boolean;
  /** Kirish. Xato bo'lsa OTADI — chaqiruvchi uni o'z ekranida ko'rsatadi. */
  readonly enter: (tenantId: number) => Promise<void>;
  readonly exit: () => Promise<void>;
}

const PartnerViewContext = createContext<PartnerViewValue | null>(null);

export function PartnerViewProvider({ children }: { readonly children: ReactNode }): ReactNode {
  const t = useT();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { reloadUser } = useAuth();

  const [tenant, setTenant] = useState<PartnerViewTenant | null>(null);
  const [expiresIn, setExpiresIn] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  /** Muddat tugaganini AYTADIGAN xabar — foydalanuvchi o'zi yopadi. */
  const [notice, setNotice] = useState<string | null>(null);

  /**
   * O'z sessiyaga qaytish — chiqish ham, muddat tugashi ham shu yerdan o'tadi.
   *
   * Ikkalasining farqi FAQAT xabarda: ish bir xil (token olib tashlanadi,
   * kesh tozalanadi, kontekst qayta o'qiladi, ro'yxatga qaytiladi). Ikki
   * alohida funksiya yozilsa, ulardan biri bir kun kesh tozalashni
   * unutardi.
   */
  const leave = useCallback(
    async (message: string | null): Promise<void> => {
      tokenStore.setPartnerView(null);
      setTenant(null);
      setExpiresIn(null);
      setNotice(message);

      /*
       * Avval MANZIL, keyin kesh: partner ekranida turib keshni tozalasak,
       * o'sha ekran darhol o'z so'rovlarini qaytadan yuborardi — endi bizning
       * token bilan — va bir zumga bizning ma'lumot partner sarlavhasi
       * ostida ko'rinardi.
       */
      await navigate({ to: '/settings/organizations' });
      queryClient.clear();

      /*
       * Kontekstni qayta o'qish YIQILSA ham chiqish BAJARILGAN bo'lib
       * qoladi: partner tokeni allaqachon olib tashlangan va so'rovlar
       * o'z sessiyamiz bilan ketyapti. Yiqilish sababi deyarli har doim
       * tarmoq bo'ladi, shuning uchun bu yerda foydalanuvchi CHIQARILMAYDI
       * — unga sabab aytiladi va kontekst sahifa yangilanganda o'zi
       * tiklanadi. Xatoni yuqoriga otish esa «Chiqish» tugmasini yiqilgan
       * ko'rsatardi, holbuki u ishini bajardi.
       */
      await reloadUser().catch(() => {
        setNotice(t('common.networkError'));
      });
    },
    [navigate, queryClient, reloadUser, t],
  );

  const enter = useCallback(
    async (tenantId: number): Promise<void> => {
      setIsBusy(true);
      try {
        // So'rov O'Z tokenimiz bilan ketadi: partner ko'rinishi hali yoqilmagan.
        const response = await api.post<PartnerViewResponse>(
          `/auth/partner-view/${String(tenantId)}`,
        );

        /*
         * Manzil ALMASHTIRILADI: turgan ekranimiz `tenant.view` talab
         * qiladi, partner tokenida esa u yo'q — o'sha yerda qolsak,
         * ekran birinchi so'rovda 403 bilan qizarardi. Bosh sahifa esa
         * har qanday tashkilotda mavjud.
         */
        await navigate({ to: '/' });

        tokenStore.setPartnerView(response.accessToken);
        queryClient.clear();
        setTenant(response.tenant);
        setExpiresIn(response.expiresIn);
        setNotice(null);

        /*
         * Ruxsatlar SERVERDAN qayta o'qiladi. Usiz menyu va tugmalar
         * bizning (yozish huquqli) ruxsatlarimizni ko'rsatib turardi va
         * xodim faqat «Saqlash» ni bosgandan keyin 403 olardi.
         */
        await reloadUser();
      } catch (error: unknown) {
        // Yarim holat qolmasin: token qo'yilgan bo'lsa ham orqaga qaytariladi.
        tokenStore.setPartnerView(null);
        setTenant(null);
        setExpiresIn(null);
        queryClient.clear();
        await reloadUser().catch(() => undefined);
        throw error;
      } finally {
        setIsBusy(false);
      }
    },
    [navigate, queryClient, reloadUser],
  );

  const exit = useCallback(async (): Promise<void> => {
    setIsBusy(true);
    try {
      await leave(null);
    } finally {
      setIsBusy(false);
    }
  }, [leave]);

  /*
   * Muddat tugaganda `api.ts` shu handlerni chaqiradi (401, refreshsiz).
   *
   * Foydalanuvchi LOGIN ekraniga TUSHMAYDI: uning o'z sessiyasi butun
   * (`accessToken` ham, `refreshToken` ham tegilmagan holda turibdi) —
   * tugagani faqat ko'rish oynasi.
   */
  useEffect(() => {
    setPartnerViewExpiredHandler(() => {
      // Handler `api.ts` ning so'rov yo'lidan chaqiriladi — u yerda kutib
      // turadigan hech kim yo'q, shuning uchun rad javobi shu yerda yutiladi.
      void leave(t('partnerView.expired')).catch(() => undefined);
    });
    return () => setPartnerViewExpiredHandler(null);
  }, [leave, t]);

  const value = useMemo<PartnerViewValue>(
    () => ({ tenant, expiresIn, isBusy, enter, exit }),
    [tenant, expiresIn, isBusy, enter, exit],
  );

  return (
    <PartnerViewContext value={value}>
      {/* Xabar kontekstda saqlanadi, lekin bannerda chiziladi: u ekran
          almashganda ham yo'qolmasligi kerak — «muddat tugadi» degan
          jumla foydalanuvchi uni o'qigunicha turishi shart. */}
      <NoticeContext value={{ notice, dismiss: () => setNotice(null) }}>{children}</NoticeContext>
    </PartnerViewContext>
  );
}

interface NoticeValue {
  readonly notice: string | null;
  readonly dismiss: () => void;
}

const NoticeContext = createContext<NoticeValue | null>(null);

export function usePartnerView(): PartnerViewValue {
  const ctx = use(PartnerViewContext);
  if (!ctx) {
    throw new Error('usePartnerView faqat <PartnerViewProvider> ichida ishlatiladi');
  }
  return ctx;
}

/**
 * BANNER — xavfsizlik xususiyatining o'zi.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ NIMA UCHUN U HAR EKRANDA VA NIMA UCHUN BOSHQA RANGDA                     ║
 * ║                                                                          ║
 * ║ Partner ko'rinishida ekrandagi HAR BIR raqam — boshqa tashkilotniki.     ║
 * ║ Qoldiq, qarz, foyda — hammasi tanish ko'rinishda, tanish joyda turadi    ║
 * ║ va ularni o'zimizniki deb o'qish uchun hech qanday sabab kerak emas.     ║
 * ║ Bitta ekranda («Partner tashkilotlar») ogohlantirish YETARLI EMAS:       ║
 * ║ odam u yerdan chiqib ketadi va oradan besh daqiqa o'tib qayerda          ║
 * ║ turganini unutadi.                                                       ║
 * ║                                                                          ║
 * ║ Shuning uchun banner karkasning ichida, yuqori panelning USTIDA turadi   ║
 * ║ va rangi ataylab boshqacha (`accent`): yon menyu va yuqori panel to'q    ║
 * ║ `chrome` rangida, kontent esa oq. Binafsha yo'l ikkalasiga ham           ║
 * ║ o'xshamaydi — ya'ni uni «odatdagi joyning bir qismi» deb o'qib           ║
 * ║ bo'lmaydi.                                                               ║
 * ║                                                                          ║
 * ║ QOG'OZGA HAM TUSHADI (`evo-no-print` YO'Q): partner ekranidan chop       ║
 * ║ etilgan varaq kimning ma'lumoti ekanini o'zi aytishi kerak, aks holda    ║
 * ║ u stolda bizning hisobotimizdan farq qilmay qoladi.                      ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */
export function PartnerViewBanner(): ReactNode {
  const t = useT();
  const { tenant, expiresIn, isBusy, exit } = usePartnerView();
  const noticeCtx = use(NoticeContext);

  if (tenant === null) {
    // Ko'rinish tugagan, lekin sababi aytilmagan bo'lsa — xabar shu yerda
    // qoladi va foydalanuvchi o'zi yopadi. O'z-o'zidan yo'qolmaydi:
    // «muddat tugadi» degan yozuvni odam o'qib ulgurishi kerak.
    if (noticeCtx?.notice == null) return null;

    return (
      <div className="px-4 pt-3 sm:px-6">
        <Alert tone="warn" onDismiss={noticeCtx.dismiss} dismissLabel={t('common.close')}>
          {noticeCtx.notice}
        </Alert>
      </div>
    );
  }

  /*
   * ╔══════════════════════════════════════════════════════════════════════════╗
   * ║ BU LENTA `accent` EMAS, `warn` RANGIDA — IKKI SABAB                     ║
   * ║                                                                          ║
   * ║ 1. ARIFMETIKA. Firma rangi logodan olingandan keyin `--color-accent`     ║
   * ║    limon (#F7FF13) bo'ldi va uning oq fondagi kontrasti 1.09 —          ║
   * ║    ya'ni bu yerdagi `text-accent` sarlavha EKRANDA UMUMAN ko'rinmasdi.   ║
   * ║                                                                          ║
   * ║ 2. MA'NO. `accent` tokenining o'z izohida «tugmaga, havolaga yoki        ║
   * ║    HOLAT nishoniga ishlatilmaydi» deb yozilgan va bu lenta aynan        ║
   * ║    holat nishoni. Ya'ni eski kod o'z qoidasini buzayotgan edi va buni    ║
   * ║    rang almashguncha hech narsa ko'rsatmadi.                             ║
   * ║                                                                          ║
   * ║ `warn` to'g'ri token: bu ogohlantirish — «siz BOSHQA tashkilotning       ║
   * ║ ma'lumotiga qarab turibsiz». `danger` RAD ETILDI: xato yuz bergani       ║
   * ║ yo'q, qilinayotgan ish qonuniy va ataylab qilinyapti; qizil lenta esa    ║
   * ║ har safar «nimadir buzildi» degan taassurot qoldirardi.                  ║
   * ╚══════════════════════════════════════════════════════════════════════════╝
   */
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b-4 border-warn bg-warn-soft px-4 py-2 sm:px-6"
    >
      <span className="text-xs font-bold uppercase tracking-wider text-warn">
        {t('partnerView.banner.title')}
      </span>

      <span className="text-sm font-semibold text-ink">
        {t('partnerView.banner.org', { code: tenant.code, name: tenant.name })}
      </span>

      {/* Uch fakt — uchtasi ham kerak va ularning hech biri ikkinchisini
          almashtirmaydi: kimniki, nima qilib bo'lmaydi, kim ko'rib turibdi. */}
      <span className="text-xs text-ink">{t('partnerView.banner.notYours')}</span>
      <span className="text-xs text-ink">{t('partnerView.banner.readOnly')}</span>
      <span className="text-xs text-ink-muted">{t('partnerView.banner.audited')}</span>

      {expiresIn !== null && (
        <span className="text-xs text-ink-muted">
          {t('partnerView.banner.window', { window: expiresIn })}
        </span>
      )}

      <span className="ml-auto">
        <Button
          size="sm"
          variant="secondary"
          disabled={isBusy}
          onClick={() => {
            void exit().catch(() => undefined);
          }}
        >
          {isBusy && <Spinner />}
          {t('partnerView.exit')}
        </Button>
      </span>
    </div>
  );
}

/**
 * «Kirib ko'rish» tugmasi — ro'yxat satrida va paket muharriri sarlavhasida.
 *
 * Xatoni O'ZI ko'rsatadi: chaqiruvchi ekranlarning ikkalasida ham xato joyi
 * boshqa-boshqa (biri jadval ichida, biri sarlavhada) va xabarni ularga
 * uzatish har ikkisiga bir xil holat qo'shishni talab qilardi.
 */
export function PartnerEnterButton({
  tenantId,
  size = 'sm',
}: {
  readonly tenantId: number;
  readonly size?: 'sm' | 'md';
}): ReactNode {
  const t = useT();
  const { tenant, isBusy, enter } = usePartnerView();
  const [error, setError] = useState<string | null>(null);

  /*
   * Partner ko'rinishida turib BOSHQA partnerga kirib bo'lmaydi: ierarxiya
   * bir pog'onali va so'rov baribir rad etilardi. Tugma shunchaki
   * ko'rsatilmaydi — bosilib xato olish foydasiz urinish.
   */
  if (tenant !== null) return null;

  return (
    <span className="inline-flex flex-col items-start gap-1">
      <Button
        size={size}
        variant="secondary"
        disabled={isBusy}
        title={t('partnerView.enterHint')}
        onClick={(event) => {
          // Satr ichidagi tugma: bosish satrni OCHMASLIGI kerak.
          event.stopPropagation();
          setError(null);
          void enter(tenantId).catch((cause: unknown) => {
            setError(isApiError(cause) ? cause.message : t('partnerView.enterFailed'));
          });
        }}
      >
        {isBusy && <Spinner />}
        {t('partnerView.enter')}
      </Button>
      {error !== null && <span className="text-xs text-danger">{error}</span>}
    </span>
  );
}
